import { History } from 'history';
import { action, observable } from 'mobx';
import { toNumber } from 'lodash';
import { TModalSignature } from '~/providers/modal.provider';
import EventEmitter from 'eventemitter3';
import { v4 as uuidv4 } from 'uuid';

// messages sent from angular to react
const ANGULAR_MESSAGE_TYPES = [
    'Loaded',
    'ModalResolved',
    'ModalRejected',
    'OpenGlobalModal',
    'Navigation',
    'Busy'
] as const;

// messages sent from react to angular
const REACT_MESSAGE_TYPES = [
    'SessionId',
    'Navigation',
    'GlobalModalResolved',
    'GlobalModalRejected'
] as const;

const REACT_MESSAGE_TYPES_SET = new Set(REACT_MESSAGE_TYPES);
type ReactMessageSubject = (typeof REACT_MESSAGE_TYPES)[number];
type AngularMessageSubject = (typeof ANGULAR_MESSAGE_TYPES)[number];
type MessagePayload = Record<string, any>;
interface BaseMessage {
    subject: AngularMessageSubject;
    sessionId: string;
    payload: MessagePayload;
}
interface BusyMessage extends BaseMessage {
    subject: 'Busy';
    payload: {
        busy: boolean;
    };
}

interface LoadedMessage extends BaseMessage {
    subject: 'Loaded';
    payload: {
        componentName: string;
        scrollHeight: number;
    };
}

interface ModalResolvedMessage extends BaseMessage {
    subject: 'ModalResolved';
    payload: {
        result: any;
    };
}

interface ModalRejectedMessage extends BaseMessage {
    subject: 'ModalRejected';
    payload: {
        error: any;
    };
}

interface NavigationMessage extends BaseMessage {
    subject: 'Navigation';
    payload: {
        path: string;
        method: 'push' | 'replace' | 'back';
    };
}

export interface OpenGlobalModalMessage extends BaseMessage {
    subject: 'OpenGlobalModal';
    payload: { componentName: keyof TModalSignature } & Record<string, any>;
}

export type AngularMessage =
    | BusyMessage
    | LoadedMessage
    | ModalResolvedMessage
    | ModalRejectedMessage
    | NavigationMessage
    | OpenGlobalModalMessage;

type MessageStack = {
    subject: ReactMessageSubject;
    payload: MessagePayload;
}[];

export class NgInReactEventEmitter extends EventEmitter<{
    onSendMessageToAngular: (data: {
        subject: ReactMessageSubject;
        payload: MessagePayload;
        sessionId: string;
    }) => void;
    onOpenGlobalModal: (payload: OpenGlobalModalMessage['payload']) => void;
}> {}

export default class NgInReactService {
    // holds messages that are sent before the angular app is loaded
    // and processes them once the angular app is loaded
    private readonly preNgLoadMessageStack: MessageStack = [];
    private sessionId = uuidv4();
    private isAngularAppLoaded = false;
    // instance of the modal rendering iframe (one instance for the entire app)
    private _modalIframe?: HTMLIFrameElement;
    // style of the modal rendering iframe, so it can be reset once modal is closed.
    private _modalIframeOriginalStyle?: CSSStyleDeclaration;
    ngInReactEventEmitter = new NgInReactEventEmitter();
    // height of the rendered angular component, used for resizing the iframe according to the rendered size so that there's no inner scroll in the iframe
    @observable ngComponentToScrollHeight?: Record<string, number> = {};
    @observable currentlyOpenModal?: {
        name: string;
        promise?: {
            resolve: (result: any) => void;
            reject: (error: any) => void;
        };
    };
    // needed to save the promise seperatly to avoid race condition issues I couldn't figure out.
    @observable currentlyOpenModalPromise?: Promise<any>;

    public sendMessage(
        subject: ReactMessageSubject,
        payload: MessagePayload
        //force = false
    ): void {
        this.ngInReactEventEmitter.emit('onSendMessageToAngular', {
            subject,
            payload,
            sessionId: this.sessionId
        });

        // Angular app is loaded
        if (this.isAngularAppLoaded && !!this._modalIframe?.contentWindow) {
            this._modalIframe.contentWindow.postMessage(
                { subject, sessionId: this.sessionId, payload },
                '*'
            );
            return;
        }

        // If not loaded, add to stack to be cleared once loaded
        const isValidMessage = REACT_MESSAGE_TYPES_SET.has(subject);
        if (isValidMessage) {
            this.preNgLoadMessageStack.push({ subject, payload });
        }
    }

    @action
    closeNgModal(modalNameToClose?: string): void {
        // Check if the currently open modal is the one that should be closed
        if (
            this.currentlyOpenModal &&
            this.currentlyOpenModal.name === modalNameToClose
        ) {
            this.currentlyOpenModal = undefined;
        }
    }

    private restoreModalIframeOriginalStyle(): void {
        if (this._modalIframe && this._modalIframeOriginalStyle) {
            Object.assign(
                this._modalIframe.style,
                this._modalIframeOriginalStyle
            );
            this._modalIframeOriginalStyle = undefined;
        }
    }

    private resolveNgModal(result: any): void {
        const promise = this.currentlyOpenModal?.promise;
        if (promise) {
            promise.resolve(result);
        }
    }

    private rejectNgModal(error: any) {
        const rejectResult =
            this.currentlyOpenModalPromise && error ? error : undefined;
        this.currentlyOpenModalPromise.catch(e => {
            if (e) {
                throw e;
            } else {
                // modal was rejected due to dismiss and with no error data,
                // so we simply ignore it
            }
        });
        this.currentlyOpenModal?.promise?.reject(rejectResult);
    }

    private handleModalResolution(cb: Function) {
        const modalNameToClose = this.currentlyOpenModal?.name;
        cb();
        this.closeNgModal(modalNameToClose);
        this.restoreModalIframeOriginalStyle();
    }

    processMessage({
        message,
        history,
        historyLog
    }: {
        message: AngularMessage;
        history: History;
        historyLog: Record<number, { source: 'angular' | 'react' }>;
    }): void {
        if (
            typeof message !== 'object' ||
            (message.subject !== 'Loaded' && !message.sessionId)
        ) {
            return;
        }

        switch (message.subject) {
            case 'Loaded':
                this.processLoaded(message);
                break;
            case 'ModalResolved':
                this.handleModalResolution(() => {
                    this.resolveNgModal(message.payload.result);
                });
                break;
            case 'ModalRejected':
                this.handleModalResolution(() => {
                    this.rejectNgModal(message.payload.error);
                });
                break;
            case 'OpenGlobalModal': {
                this.ngInReactEventEmitter.emit(
                    'onOpenGlobalModal',
                    message.payload
                );

                break;
            }
            case 'Navigation':
                this.handleNavigation({
                    message,
                    history,
                    historyLog
                });
                break;
            case 'Busy':
                break;
        }
    }

    private handleNavigation({
        message,
        history,
        historyLog
    }: {
        message: NavigationMessage;
        history: History;
        historyLog: Record<number, { source: 'angular' | 'react' }>;
    }) {
        if (message.payload.path === '/login') {
            return;
        }

        this.clearStaleLogs(historyLog, history.length);

        switch (message.payload.method) {
            case 'replace':
                history.replace(message.payload.path);
                break;
            case 'push':
                history.push(message.payload.path); // This sync the iframe route with the react app
                historyLog[history.length] = { source: 'angular' }; // This allow us to distinct pushed state that is angular state
                break;
            case 'back':
                // When we go back in angular we also want to go back in react, it wouldn't affect the history stack
                // because we are still in an angular first route but when we will arrive to a react first state the
                // react app will take control
                if (historyLog[history.length]) {
                    history.goBack();
                    delete historyLog[history.length];
                }
                break;
        }
    }

    private clearStaleLogs(
        historyLog: Record<number, { source: 'angular' | 'react' }>,
        historyLength: number
    ) {
        Object.keys(historyLog).forEach(key => {
            if (toNumber(key) > historyLength) {
                delete historyLog[toNumber(key)]; // Remove all future history entries
            }
        });
    }

    @action
    private processLoaded(message: LoadedMessage): void {
        this.isAngularAppLoaded = true;
        if (message.payload?.componentName) {
            this.ngComponentToScrollHeight = {
                [message.payload.componentName]: message.payload.scrollHeight
            };
        }
        this.sendMessage('SessionId', { sessionId: this.sessionId });
        this.processPreNgLoadMessageStack();
    }

    private processPreNgLoadMessageStack(): void {
        while (this.preNgLoadMessageStack.length) {
            const message = this.preNgLoadMessageStack.pop();

            if (message) {
                this.sendMessage(message.subject, message.payload);
            }
        }
    }

    setModalIframe(iframe: HTMLIFrameElement | null): void {
        if (!iframe) {
            return;
        }

        this._modalIframe = iframe;
    }

    @action
    private setCurrentlyOpenModal(
        modalName: string,
        resolve?: (result: any) => void,
        reject?: (error: any) => void
    ): void {
        this.currentlyOpenModal = {
            name: modalName,
            promise: {
                resolve: resolve,
                reject: reject
            }
        };
    }

    @action
    private setCurrentlyOpenModalPromise(promise: Promise<any>): void {
        this.currentlyOpenModalPromise = promise;
    }

    openAngularModal<PromiseResult = unknown>({
        modalServiceMethodName,
        modalParams,
        iFrameStyle
    }: {
        modalServiceMethodName: string;
        modalParams?: any;
        iFrameStyle?: Partial<CSSStyleDeclaration>;
    }): Promise<PromiseResult> {
        if (this._modalIframe) {
            this.sendMessage('Navigation', {
                path: `ng_in_react.modal_opener`,
                params: {
                    modalServiceMethodName,
                    modalParams: JSON.stringify(modalParams || {})
                }
            });

            if (iFrameStyle) {
                this._modalIframeOriginalStyle = this._modalIframe.style;
                Object.assign(this._modalIframe.style, iFrameStyle);
            }

            const promise = new Promise<PromiseResult>((resolve, reject) => {
                this.setCurrentlyOpenModal(
                    modalServiceMethodName,
                    resolve,
                    reject
                );
            });
            this.setCurrentlyOpenModalPromise(promise);

            return promise;
        }

        return Promise.reject('modal iframe not found');
    }
}
